import React, { useState } from "react"
import type { FC } from "react"
import { Outlet } from "react-router"

import { ImpersonateAlert } from "@/components/impersonate-alert"
import { useDisclosure } from "@/nzds/utils/use-disclosure"
import { MAIN_MENU_TOGGLE_HEIGHT, MAIN_MENU_WIDTH } from "@/utils/constants"

import MenuRoundedIcon from "@mui/icons-material/MenuRounded"
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded"
import { Box, IconButton, Stack, Tooltip, styled } from "@mui/material"
import { visuallyHidden } from "@mui/utils"

import { MainNavList } from "./main-nav-list"
import { NavigationDrawer } from "./navigation-drawer/navigation-drawer"

const MainNavBar = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.spectrum.blue[800],
  color: theme.palette.common.white,
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  left: 0,
  maxWidth: MAIN_MENU_WIDTH,
  position: "fixed",
  top: 0,
})) as typeof Box

export const AuthenticatedLayout: FC = () => {
  const { isOpen: drawerIsOpen, toggle: drawerToggle } = useDisclosure()
  const [isSkipNavigationVisible, setIsSkipNavigationVisible] = useState(false)

  const handleSkipNavigationBlur = () => {
    setIsSkipNavigationVisible(false)
  }

  const handleSkipNavigationKeyboardFocus = () => {
    setIsSkipNavigationVisible(true)
  }

  return (
    <>
      <Stack
        direction="row"
        sx={{
          alignItems: "flex-start",
        }}
      >
        <MainNavBar aria-label="Main navigation" component="nav">
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack
              sx={{
                justifyContent: "center",
                height: MAIN_MENU_TOGGLE_HEIGHT,
                minHeight: MAIN_MENU_TOGGLE_HEIGHT,
              }}
            >
              <IconButton
                aria-label="Menu"
                aria-pressed={drawerIsOpen}
                color="inherit"
                onClick={drawerToggle}
              >
                <MenuRoundedIcon />
              </IconButton>
            </Stack>

            <Tooltip placement="right" title="Skip navigation">
              <IconButton
                color="inherit"
                href="#mainContent"
                onBlur={handleSkipNavigationBlur}
                onFocusVisible={handleSkipNavigationKeyboardFocus}
                sx={{
                  ...(isSkipNavigationVisible ? {} : visuallyHidden),
                  marginBottom: 1,
                }}
              >
                <SkipNextRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <MainNavList variant="compact" />
        </MainNavBar>
        <Stack
          component="main"
          id="mainContent"
          sx={{
            flex: 1,
            marginLeft: MAIN_MENU_WIDTH,
            minHeight: "100vh",
            minWidth: 0,
            position: "relative",
          }}
        >
          <ImpersonateAlert />
          <Outlet />
        </Stack>
      </Stack>
      <NavigationDrawer open={drawerIsOpen} toggleDrawer={drawerToggle} />
    </>
  )
}
